import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { AlertCircle, X } from 'lucide-react';
import { Button } from './Button';
import { useLanguageStore } from '../../store/languageStore';

interface ErrorMessageProps {
  message: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  variant?: 'default' | 'compact' | 'banner';
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({
  message,
  onRetry,
  onDismiss,
  className = '',
  variant = 'default',
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();

  if (variant === 'compact') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={`inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md ${className}`}
      >
        <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
        <span className="text-sm text-red-700 font-medium">{message}</span>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="ml-2 text-red-400 hover:text-red-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </motion.div>
    );
  }

  if (variant === 'banner') {
    const patternUrl = "data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E";
    
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`relative overflow-hidden bg-gradient-to-r from-red-500 via-red-600 to-red-500 text-white shadow-lg ${className}`}
      >
        <div 
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: `url("${patternUrl}")` }}
        ></div>
        <div className="relative flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ rotate: [0, -10, 10, -10, 0] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
            >
              <AlertCircle className="h-6 w-6 flex-shrink-0" />
            </motion.div>
            <div>
              <p className="font-semibold text-sm mb-0.5">{t('common.error')}</p>
              <p className="text-sm opacity-95">{message}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onRetry && (
              <Button
                variant="text"
                size="sm"
                onClick={onRetry}
                className="text-white hover:bg-white/20 border-white/30"
              >
                {t('common.retry')}
              </Button>
            )}
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="p-1.5 hover:bg-white/20 rounded-md transition-colors"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      className={`relative overflow-hidden bg-white border border-red-200 rounded-xl shadow-lg ${className}`}
    >
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-red-50 opacity-50"></div>
      
      {/* Left accent bar with pulse animation */}
      <motion.div
        className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-red-500 to-red-600"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Pulse effect */}
      <motion.div
        className="absolute left-0 top-0 bottom-0 w-1 bg-red-400"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
      />

      <div className="relative flex items-start gap-4 p-5">
        {/* Icon container with background */}
        <motion.div
          className="flex-shrink-0"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-red-100 rounded-full blur-md opacity-50"></div>
            <div className="relative bg-gradient-to-br from-red-500 to-red-600 rounded-full p-2.5 shadow-lg">
              <AlertCircle className="h-5 w-5 text-white" />
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
          <motion.h3
            initial={{ opacity: 0, x: isRTL ? 10 : -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm font-bold text-red-900 mb-1.5 flex items-center gap-2"
          >
            <span>{t('common.error')}</span>
            <motion.span
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="text-red-500"
            >
              ●
            </motion.span>
          </motion.h3>
          
          <motion.p
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-sm text-red-800 leading-relaxed mb-3"
          >
            {message}
          </motion.p>

          {/* Actions */}
          <AnimatePresence>
            {(onRetry || onDismiss) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ delay: 0.4 }}
                className={`flex items-center gap-2 ${isRTL ? 'justify-end' : 'justify-start'}`}
              >
                {onRetry && (
                  <Button
                    variant="text"
                    size="sm"
                    onClick={onRetry}
                    className="text-red-700 hover:text-red-900 hover:bg-red-50 font-medium px-3 py-1.5"
                  >
                    {t('common.retry')}
                  </Button>
                )}
                {onDismiss && (
                  <button
                    onClick={onDismiss}
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                    aria-label="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

